import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { Subject, Subscription, concatMap, forkJoin, tap, throwError } from 'rxjs';

import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { DistributionRulesService } from '@services/distribution-rules/1.0/distribution-rules.service';
import { Bonus, DistributionRule, Reward, Tenant, Tier } from '@generated/tcea/models';
import { SelectionModel } from '@angular/cdk/collections';
import { MessageType } from '@constants/message/1.0';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { LoyaltyService } from '@services/loyalty/1.0/loyalty.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { GetService } from '@services/utilities/1.0/get.service';
import { MatDialogTitle } from '@angular/material/dialog';
import { NgIf, DatePipe } from '@angular/common';
import { MessageComponent } from '../message/1.0/message.component';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ButtonComponent } from '../button/1.0/button.component';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MatCheckbox } from '@angular/material/checkbox';
const defaultFilters = {
  availabilityStart: undefined,
  availabilityEnd: undefined,
  search: '',
  enabled: undefined,
  type: undefined,
};

@Component({
  selector: 'txp-distribution-rules',
  templateUrl: './distribution-rules.component.html',
  styleUrls: ['./distribution-rules.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    NgIf,
    MessageComponent,
    MatFormField,
    MatPrefix,
    MatInput,
    ButtonComponent,
    LoadingComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
    DatePipe,
  ],
})
export class DistributionRulesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;

  messageType = MessageType; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  rules: DistributionRule[] = [];
  dataSource: MatTableDataSource<DistributionRule> = new MatTableDataSource<DistributionRule>();

  itemsList = [];
  isLoading = false;
  loadingMore = false;
  subscription: Subscription;
  audienceMap = {
    USERS: 'app.nav.users',
    SEGMENTS: 'coupons.rules.grid.fields.segments',
  };
  enabledMap = {
    true: 'form.button.yes',
    false: 'form.button.no',
  };
  frequencyMap = {
    DAILY: 'coupons.rules.grid.edit.schedule.frequency.day',
    MONTHLY: 'coupons.rules.grid.edit.schedule.frequency.month',
    YEARLY: 'coupons.rules.grid.edit.schedule.frequency.year',
    WEEKLY: 'coupons.rules.grid.edit.schedule.frequency.week',
  };
  typeMap = {
    ONE_TIME: 'coupons.rules.grid.fields.type.oneTime',
    RECURRING: 'coupons.rules.grid.fields.type.recurring',
    EVENT: 'coupons.rules.grid.fields.type.event',
  };
  displayColumns: string[] = [
    'select',
    'name',
    'type',
    'audience',
    'enabled',
    'schedule.startDate',
    'schedule.endDate',
    'schedule.frequency',
    'createdOn',
  ];
  isStatusChange: boolean;
  selection = new SelectionModel(true, [], true);
  rulesReadOnly = true;
  brandId: Tenant['id'];
  loadingError = false;
  enableError = false;
  enableDistributionRuleSuccess = false;
  disableDistributionRuleSuccess = false;
  emptyRulesList = false;
  archiveError = false;
  archiveSuccess = false;
  page = 0;
  isLastPage = false;
  searchClicked = false;
  emptySearchList = false;
  loyaltyRule = false;
  programId: string;
  bonusLookup: { [key: string]: Tier } = {};
  rewardsLookup: { [key: string]: Reward } = {};

  filters = { ...defaultFilters };

  private unsubscribe = new Subject<void>();

  constructor(
    private distributionRulesService: DistributionRulesService,
    private loyaltyService: LoyaltyService,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private translateService: TranslateService,
    private permissionsService: PermissionsService,
  ) {
    this.rulesReadOnly = !this.permissionsService.getPermission('coupon-management:write');
  }

  ngOnInit() {
    this.loyaltyRule = this.router?.url?.includes('loyalty');
    this.brandId = window.sessionStorage.getItem('brand_id');
    this.dataSource.filterPredicate = (el: DistributionRule, filter: string) =>
      el.name.toLowerCase().includes(filter.toLowerCase());

    if (this.brandId) {
      if (this.loyaltyRule) {
        this.programId = this.router.url?.split('/')[3];
        this.reset();
        this.isLoading = true;
        this.subscription = this.getLoyaltyItems()
          .pipe(concatMap(() => this.getDistributionRules()))
          .subscribe(this.getSubscriptionHandlers());
      } else {
        this.reset();
        this.isLoading = true;
        this.getDistributionRules().subscribe(this.getSubscriptionHandlers());
      }
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    // unsubscribe so it doesn't keep fetching coupons
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onSortChange(sort): void {
    const data: Array<DistributionRule> = this.dataSource.data.slice();

    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc: boolean = sort.direction === 'asc';
      const valSortProperty = sort.active;
      const aValue = GetService.get(a, valSortProperty);
      const bValue = GetService.get(b, valSortProperty);
      return aValue && bValue ? this.compare(aValue, bValue, isAsc) : -1;
    });

    this.dataSource = new MatTableDataSource<DistributionRule>(this.dataSource.data);
  }

  compare(a: any, b: any, isAsc: boolean): number {
    if (isAsc) {
      return a > b ? 1 : b > a ? -1 : 0;
    } else {
      return a < b ? 1 : b < a ? -1 : 0;
    }
  }

  /**
   * Get all distribution rules for the given brandId and populate the data source as the data
   * is observed
   */
  getDistributionRules() {
    return this.distributionRulesService
      .getRules(this.brandId, { page: this.page, filter: [''] })
      .pipe(
        tap(({ data, pages }) => {
          this.isLastPage = this.page + 1 >= pages;
          this.rules = [...this.rules, ...data];
          if (this.rules.length > 0 && this.loyaltyRule) {
            this.rules = this.rules.filter(
              (rule) => rule.channel === 'LOYALTY_REWARDS' || rule.channel === 'LOYALTY_POINTS',
            );

            this.rules = this.rules.filter((rule) => {
              let channelId = rule.channelIds[0]?.split('|')[1]?.trim();
              if (rule.channel === 'LOYALTY_REWARDS') {
                return this.rewardsLookup[channelId];
              } else if (rule.channel === 'LOYALTY_POINTS') {
                return this.bonusLookup[channelId];
              }
            });
          } else {
            this.rules = this.rules.filter((rule) => rule.channel === 'COUPON_WALLET');
          }

          this.dataSource.data = [...this.rules];
          this.loadingMore = false;
          if (!this.dataSource.data.length) {
            this.searchClicked ? (this.emptySearchList = true) : (this.emptyRulesList = true);
          }
        }),
      );
  }

  getLoyaltyItems() {
    const rewards = this.loyaltyService.getAllRewards(this.brandId, this.programId);
    const bonuses = this.loyaltyService.getAllBonuses(this.brandId, this.programId);

    return forkJoin({ rewards, bonuses }).pipe(
      tap((data: { rewards: Reward[]; bonuses: Bonus[] }) => {
        this.bonusLookup = this.utilitiesService.convertArrayToMap(data.bonuses, 'id');
        this.rewardsLookup = this.utilitiesService.convertArrayToMap(data.rewards, 'id');
      }),
    );
  }

  private getSubscriptionHandlers() {
    return {
      error: () => {
        this.loadingError = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    };
  }

  searchDistributionRules() {
    this.searchClicked = true;
    this.rules = [];
    this.page = 0;
    this.isLastPage = false;
    this.getDistributionRules();
  }

  loadMoreRules() {
    if (this.isLoading || this.isLastPage) {
      return;
    }

    this.loadingMore = true;
    this.page = this.page + 1;
    this.getDistributionRules();
  }

  editRule(rule: DistributionRule) {
    if (this.loyaltyRule) {
      this.router.navigateByUrl(`loyalty/program/${this.programId}/distribution-rules/${rule.id}`, {
        state: { data: rule },
      });
    } else {
      this.router.navigateByUrl(`/coupons/distribution-rules/${rule.id}`, {
        state: { data: rule },
      });
    }
  }

  createRule(): void {
    if (this.loyaltyRule) {
      this.router.navigateByUrl(`loyalty/program/${this.programId}/distribution-rules/new`);
    } else {
      this.router.navigateByUrl(`/coupons/distribution-rules/new`);
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      for (const row of this.dataSource.filteredData) {
        this.selection.select(row);
      }
    }
  }

  filter(value) {
    this.dataSource.filter = value.trim().toLowerCase();
    // apply filter to selected items as well, clearing items that do not match
    this.selection.selected.forEach((selected) => {
      const matchesFilter = this.dataSource.filterPredicate(selected, value);
      if (!matchesFilter) {
        this.selection.deselect(selected);
      }
    });
  }

  resetFilters() {
    this.rules = [];
    this.page = 0;
    this.isLastPage = false;
    this.filters = { ...defaultFilters };
    this.getDistributionRules();
  }

  reset() {
    this.loadingError = false;
    this.emptyRulesList = false;
    this.emptySearchList = false;
    this.enableError = false;
    this.archiveError = false;
    this.archiveSuccess = false;
    this.enableDistributionRuleSuccess = false;
    this.disableDistributionRuleSuccess = false;
  }
}
